import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
const GoogleProfileConfirmModal = ({ isOpen, googleData, onConfirm, onCancel }) => {
  const { t } = useTranslation('shared');
  const [useGoogleData, setUseGoogleData] = useState(true);
  const [customName, setCustomName] = useState(googleData?.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !googleData) return null;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const finalData = {
        ...googleData,
        name: useGoogleData ? googleData.name : customName.trim() || googleData.email.split('@')[0],
        avatarUrl: useGoogleData ? googleData.avatarUrl : null,
      };
      await onConfirm(finalData);
    } catch (error) {
      console.error('Confirm error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-[#050a16]/70 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 12 }}
            transition={{ duration: 0.22 }}
            className="w-full max-w-md mx-4 rounded-2xl border border-[#3d8ef0]/24 bg-[#0d1424]/88 p-7 shadow-[0_18px_50px_rgba(5,10,22,0.55)] backdrop-blur-[12px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {t('googleProfileConfirmModal.title')}
              </h2>
              <button
                onClick={onCancel}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <X className="w-5 h-5 text-[#8aaccc]" />
              </button>
            </div>

            <div className="mb-5 p-4 rounded-xl bg-[#080e1a]/60 border border-white/10">
              <div className="flex items-center gap-4">
                {googleData.avatarUrl ? (
                  <img
                    src={googleData.avatarUrl}
                    alt={googleData.name}
                    className="w-14 h-14 rounded-full object-cover border border-white/10"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#3d8ef0]/20 flex items-center justify-center">
                    <User className="w-7 h-7 text-[#5ca3f5]" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-white">{googleData.name}</p>
                  <p className="text-sm text-[#8aaccc]">{googleData.email}</p>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${useGoogleData ? 'border-[#3d8ef0]/50 bg-[#3d8ef0]/10' : 'border-white/10 hover:bg-white/[0.03]'}`}
              >
                <input
                  type="radio"
                  checked={useGoogleData}
                  onChange={() => setUseGoogleData(true)}
                  className="w-4 h-4 accent-[#3d8ef0]"
                />
                <div className="flex-1">
                  <p className="font-medium text-white">
                    {t('googleProfileConfirmModal.useGoogleTitle')}
                  </p>
                  <p className="text-sm text-[#8aaccc]">
                    {t('googleProfileConfirmModal.useGoogleDescription')}
                  </p>
                </div>
                {useGoogleData && (
                  <Check className="w-5 h-5 text-[#3d8ef0]" />
                )}
              </label>
            </div>

            <div className="mb-6">
              <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${!useGoogleData ? 'border-[#3d8ef0]/50 bg-[#3d8ef0]/10' : 'border-white/10 hover:bg-white/[0.03]'}`}
              >
                <input
                  type="radio"
                  checked={!useGoogleData}
                  onChange={() => setUseGoogleData(false)}
                  className="w-4 h-4 accent-[#3d8ef0]"
                />
                <div className="flex-1">
                  <p className="font-medium text-white">
                    {t('googleProfileConfirmModal.useCustomTitle')}
                  </p>
                  <p className="text-sm text-[#8aaccc]">
                    {t('googleProfileConfirmModal.useCustomDescription')}
                  </p>
                </div>
                {!useGoogleData && (
                  <Check className="w-5 h-5 text-[#3d8ef0]" />
                )}
              </label>

              {!useGoogleData && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3"
                >
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder={t('googleProfileConfirmModal.namePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-[#080e1a]/70 text-white outline-none focus:border-[#3d8ef0]/70"
                  />
                </motion.div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 rounded-xl border border-white/10 bg-[#080e1a]/50 text-[#c5d6ea] font-medium hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('googleProfileConfirmModal.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting || (!useGoogleData && !customName.trim())}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[#3d8ef0] to-[#5ca3f5] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('googleProfileConfirmModal.saving')}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {t('googleProfileConfirmModal.continue')}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GoogleProfileConfirmModal;



